/**
 * CanonicalPersistenceFormat — long-term storage format for canonical
 * recordings. v3 supersedes the legacy `PoseBinaryFormat` (v1 / v2)
 * which only stored skeleton landmarks.
 *
 * v3 stores:
 *   - StreamHeader (full session metadata, immutable)
 *   - SubjectProfile (host-managed, optional, possibly post-hoc)
 *   - CanonicalFrame stream (gzipped JSON for now; binary SoA layer
 *     comes in a follow-up revision when the schema settles)
 *
 * v3.1 (Phase 12): adds optional `lifted3D` from a 2D→3D PoseLifter.
 * v3.2 (Phase 13): adds optional `scene` SceneCalibration recovered
 *                  from a single-frame monocular depth pass.
 *
 * Backwards compatibility: any reader checking
 * `formatVersion.startsWith('3.')` continues to load older recordings;
 * the new fields default to null when missing. Forward compatibility:
 * older readers that accessed lifted3D / scene as `undefined` continue
 * to work.
 *
 * docs/architecture-spec.md §4.2, §9.
 * docs/migration-plan.md Phase 7, 12, 13.
 */

import { gunzipSync, gzipSync } from 'fflate';

import {
  CANONICAL_SCHEMA_VERSION,
  type CanonicalFrame,
  type StreamHeader,
  type SubjectProfile,
} from '../canonical';
import type { Lifted3DSeries } from '../core/PoseLifter';
import type { SceneCalibration } from '../core/SubjectScaleEstimator';

const FORMAT_VERSION = '3.2.0';
const MAGIC = 'BNL3';

export interface CanonicalRecording {
  /** Persistent format version (independent of the canonical schema). */
  formatVersion: string;
  /** Canonical schema version of the embedded frames. */
  schemaVersion: string;
  /** Optional cross-session correlator. */
  sessionId: string | null;
  header: StreamHeader;
  subject: SubjectProfile | null;
  frames: CanonicalFrame[];
  /** v3.1+. Optional 2D→3D lifter output. null when no lifter ran or
   *  the sequence was shorter than one window. */
  lifted3D: Lifted3DSeries | null;
  /** v3.2+. Optional single-frame scene calibration. null when the
   *  depth pass was skipped or no body / depth was recoverable. */
  scene: SceneCalibration | null;
}

interface OnDiskShape {
  magic: typeof MAGIC;
  formatVersion: string;
  schemaVersion: string;
  sessionId: string | null;
  header: StreamHeader;
  subject: SubjectProfile | null;
  frames: CanonicalFrame[];
  lifted3D?: Lifted3DSeries | null;
  scene?: SceneCalibration | null;
}

export function encodeCanonicalRecording(rec: CanonicalRecording): Uint8Array {
  const payload: OnDiskShape = {
    magic: MAGIC,
    formatVersion: rec.formatVersion,
    schemaVersion: rec.schemaVersion,
    sessionId: rec.sessionId,
    header: rec.header,
    subject: rec.subject,
    frames: rec.frames.map((f) => ({
      ...f,
      camera: f.camera
        ? {
            ...f.camera,
            world_T_camera: Array.from(f.camera.world_T_camera) as unknown as Float32Array,
          }
        : null,
      world: f.world
        ? {
            ...f.world,
            anchors: f.world.anchors.map((a) => ({
              ...a,
              pose: Array.from(a.pose) as unknown as Float32Array,
            })),
          }
        : null,
    })),
    lifted3D: rec.lifted3D,
    scene: rec.scene,
  };
  const json = JSON.stringify(payload);
  return gzipSync(new TextEncoder().encode(json));
}

export function decodeCanonicalRecording(bytes: Uint8Array): CanonicalRecording {
  const text = new TextDecoder().decode(gunzipSync(bytes));
  const parsed = JSON.parse(text) as OnDiskShape;
  if (parsed.magic !== MAGIC) {
    throw new Error('CanonicalPersistenceFormat: not a v3 file (missing magic)');
  }
  if (!parsed.formatVersion.startsWith('3.')) {
    throw new Error(
      `CanonicalPersistenceFormat: unsupported format version ${parsed.formatVersion}`,
    );
  }
  // Re-hydrate Float32Array members.
  for (const f of parsed.frames) {
    if (f.camera) {
      f.camera.world_T_camera = new Float32Array(
        f.camera.world_T_camera as unknown as ArrayLike<number>,
      );
    }
    if (f.world) {
      for (const a of f.world.anchors) {
        a.pose = new Float32Array(a.pose as unknown as ArrayLike<number>);
      }
    }
  }
  return {
    formatVersion: parsed.formatVersion,
    schemaVersion: parsed.schemaVersion,
    sessionId: parsed.sessionId,
    header: parsed.header,
    subject: parsed.subject,
    frames: parsed.frames,
    // v3.0 / v3.1 / v3.2 readability: missing fields default to null.
    lifted3D: parsed.lifted3D ?? null,
    scene: parsed.scene ?? null,
  };
}

export const CANONICAL_FORMAT_VERSION = FORMAT_VERSION;

/**
 * Helper to construct a recording from in-memory canonical pieces.
 * Lifted3D and SceneCalibration are optional — pass `null` when not
 * available; omit entirely for the legacy v3.0 shape.
 */
export function makeCanonicalRecording(
  header: StreamHeader,
  frames: CanonicalFrame[],
  subject: SubjectProfile | null = null,
  sessionId: string | null = null,
  lifted3D: Lifted3DSeries | null = null,
  scene: SceneCalibration | null = null,
): CanonicalRecording {
  return {
    formatVersion: FORMAT_VERSION,
    schemaVersion: CANONICAL_SCHEMA_VERSION,
    sessionId,
    header,
    subject,
    frames,
    lifted3D,
    scene,
  };
}
